import type { Tone } from '../theme/tokens';

/** One tag the recognition service attached to an upload (`/mockup/events` → `tags[]`). */
export interface MockupTag {
    tagId: number;
    tagName: string;
    /** 1 when the tag is shown to the customer, 0 when it is internal only. */
    visible?: number | boolean | null;
    category?: string | null;
    subcategoryId?: number | null;
}

/** An upload event as the admin API returns it. Only the fields the UI reads are typed. */
export interface MockupEvent {
    eventId?: number | string;
    id?: number | string;
    tempEventId?: string | null;
    customerId?: number | string | null;
    sessionId?: number | string | null;
    createdAt?: string | null;
    status?: string | null;
    additionalEvents?: number | null;
    mockupUrl?: string | null;
    imageUrl?: string | null;
    imageUrl2?: string | null;
    saveUrl?: string | null;
    toCart?: number | boolean | null;
    toCartDate?: string | null;
    orderExist?: number | boolean | null;
    ip?: string | null;
    product?: string | null;
    productSku?: string | null;
    productId?: number | null;
    urlPath?: string | null;
    urlPathId?: number | null;
    template?: string | null;
    imageRecognized?: number | boolean | null;
    recognitionStatus?: string | null;
    typeId?: number | null;
    generationTypeId?: number | null;
    errorMessage?: string | null;
    tags?: MockupTag[] | null;
}

export const asFlag = (value: unknown): boolean => value === 1 || value === true || value === '1';

export const mockupEventId = (event: MockupEvent | null | undefined): string | number | null =>
    event?.eventId ?? event?.id ?? event?.tempEventId ?? null;

/** Pipeline status (`OK`, `PENDING`, `ERR_*`) as a chip label and tone. */
export const mockupStatus = (status: string | null | undefined): { label: string; tone: Tone } => {
    switch (status) {
        case 'OK':
            return { label: 'OK', tone: 'success' };
        case 'PENDING':
            return { label: 'Pending', tone: 'warning' };
        case 'ERR_MOCKUP':
            return { label: 'Mockup error', tone: 'danger' };
        case 'ERR_REMOVEBG':
            return { label: 'Remove.bg error', tone: 'danger' };
        case 'ERR_NETWORK':
            return { label: 'Network error', tone: 'danger' };
        default:
            return { label: status || 'Unknown', tone: 'neutral' };
    }
};

export const isTestTemplate = (event: MockupEvent | null | undefined): boolean => event?.template === 'ab-test';

const CATEGORY_ORDER = ['objectCount', 'generalClass', 'objectsClasses', 'objectSpecific'];

/** Visible tags first, then the historical category order, then by name. */
export const sortMockupTags = (tags: MockupTag[] | null | undefined): MockupTag[] => {
    if (!tags) return [];
    return [...tags].sort((a, b) => {
        const visibleDiff = Number(asFlag(b.visible)) - Number(asFlag(a.visible));
        if (visibleDiff !== 0) return visibleDiff;
        const ai = CATEGORY_ORDER.indexOf(a.category || '');
        const bi = CATEGORY_ORDER.indexOf(b.category || '');
        const catDiff = (ai === -1 ? CATEGORY_ORDER.length : ai) - (bi === -1 ? CATEGORY_ORDER.length : bi);
        if (catDiff !== 0) return catDiff;
        return String(a.tagName).localeCompare(String(b.tagName));
    });
};

/** Share of `part` in `whole`, as "12.3%"; "—" when there is nothing to divide by. */
export const percentOf = (part: number, whole: number, digits = 1): string =>
    whole > 0 ? `${((part / whole) * 100).toFixed(digits)}%` : '—';

/** Only http(s) URLs are rendered as links or images; anything else is shown as text. */
export const safeHref = (url: string | null | undefined): string | null =>
    typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
